import { supabase } from './supabase';
import { TasksBoard, CreateBoardPayload, BoardUser, UserTypeInfo, TaskCard, BoardWithColumns } from '../types/board';

interface DashboardData {
  boards: TasksBoard[];
  defaultBoard: TasksBoard | null;
  boardData: BoardWithColumns | null;
  userType: 'admin' | 'resource';
  resourceId?: string;
  resourceName?: string;
}

/**
 * Get resource name by ID
 */
async function getResourceName(resourceId: string): Promise<string> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  console.log('Fetching resource name for ID:', resourceId);
  
  const { data, error } = await supabase
    .from('resources')
    .select('name')
    .eq('id', resourceId)
    .single();
  
  if (error) {
    console.error('Detailed error fetching resource name:', {
      error,
      code: error.code,
      message: error.message,
      details: error.details,
      hint: error.hint,
      resourceId
    });
    throw new Error(`Failed to fetch resource name: ${error.message}`);
  }
  
  console.log('Resource name fetched successfully:', data.name);
  return data.name;
}

/**
 * Create a new board
 */
export async function createBoard(
  businessId: string,
  boardTitle: string,
  userInfo: UserTypeInfo
): Promise<TasksBoard> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  try {
    // Create the assigned user based on user type
    let assignedUser: BoardUser;
    
    if (userInfo.userType === 'admin') {
      // Admin creates board with "business" user type
      assignedUser = {
        type: 'business',
        id: null,
        name: 'Admin'
      };
    } else {
      // Resource creates board with their own info
      if (!userInfo.resourceId) {
        throw new Error('Resource ID is required for resource users');
      }
      
      // Get resource name from database
      const resourceName = await getResourceName(userInfo.resourceId);
      
      assignedUser = {
        type: 'resource',
        id: userInfo.resourceId,
        name: resourceName
      };
    }

    // Create the board
    console.log('Creating board with data:', {
      business_id: businessId,
      board_title: boardTitle,
      assigned_users: [assignedUser]
    });

    const { data, error } = await supabase
      .from('tasks_board')
      .insert({
        business_id: businessId,
        board_title: boardTitle,
        assigned_users: [assignedUser]
      })
      .select()
      .single();

    if (error) {
      console.error('Detailed error creating board:', {
        error,
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      throw new Error(`Failed to create board: ${error.message}`);
    }

    console.log('Board created successfully:', data);
    return data;
  } catch (error) {
    console.error('Error in createBoard:', error);
    throw error;
  }
}

/**
 * Get all boards for a business
 */
export async function getBoards(businessId: string): Promise<TasksBoard[]> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  try {
    const { data, error } = await supabase
      .from('tasks_board')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching boards:', error);
      throw new Error('Failed to fetch boards');
    }

    return data || [];
  } catch (error) {
    console.error('Error in getBoards:', error);
    throw error;
  }
}

/**
 * Get boards that a user has access to
 */
export async function getUserBoards(businessId: string, userInfo: UserTypeInfo): Promise<TasksBoard[]> {
  try {
    const allBoards = await getBoards(businessId);
    
    // Filter boards based on user permissions
    return allBoards.filter(board => {
      return board.assigned_users.some(user => {
        if (userInfo.userType === 'admin') {
          // Admins can see all boards created by any admin (business type)
          return user.type === 'business';
        } else {
          // Resources can see boards where they are assigned
          return user.type === 'resource' && user.id === userInfo.resourceId;
        }
      });
    });
  } catch (error) {
    console.error('Error in getUserBoards:', error);
    throw error;
  }
}

/**
 * Update board title
 */
export async function updateBoardTitle(boardId: string, newTitle: string): Promise<TasksBoard> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  try {
    const { data, error } = await supabase
      .from('tasks_board')
      .update({ board_title: newTitle })
      .eq('id', boardId)
      .select()
      .single();

    if (error) {
      console.error('Error updating board title:', error);
      throw new Error('Failed to update board title');
    }

    return data;
  } catch (error) {
    console.error('Error in updateBoardTitle:', error);
    throw error;
  }
}

/**
 * Add user to board
 */
export async function addUserToBoard(boardId: string, user: BoardUser): Promise<TasksBoard> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  try {
    // First get the current board
    const { data: currentBoard, error: fetchError } = await supabase
      .from('tasks_board')
      .select('*')
      .eq('id', boardId)
      .single();

    if (fetchError) {
      console.error('Error fetching current board:', fetchError);
      throw new Error('Failed to fetch current board');
    }

    // Check if user is already assigned
    const isAlreadyAssigned = currentBoard.assigned_users.some((assignedUser: BoardUser) => 
      assignedUser.type === user.type && assignedUser.id === user.id
    );

    if (isAlreadyAssigned) {
      throw new Error('User is already assigned to this board');
    }

    // Add the new user to assigned_users
    const updatedUsers = [...currentBoard.assigned_users, user];

    const { data, error } = await supabase
      .from('tasks_board')
      .update({ assigned_users: updatedUsers })
      .eq('id', boardId)
      .select()
      .single();

    if (error) {
      console.error('Error adding user to board:', error);
      throw new Error('Failed to add user to board');
    }

    return data;
  } catch (error) {
    console.error('Error in addUserToBoard:', error);
    throw error;
  }
}

/**
 * Remove user from board
 */
export async function removeUserFromBoard(boardId: string, userToRemove: BoardUser): Promise<TasksBoard> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  try {
    // First get the current board
    const { data: currentBoard, error: fetchError } = await supabase
      .from('tasks_board')
      .select('*')
      .eq('id', boardId)
      .single();

    if (fetchError) {
      console.error('Error fetching current board:', fetchError);
      throw new Error('Failed to fetch current board');
    }

    // Remove the user from assigned_users
    const updatedUsers = currentBoard.assigned_users.filter((user: BoardUser) => 
      !(user.type === userToRemove.type && user.id === userToRemove.id)
    );

    // Don't allow removing the last user
    if (updatedUsers.length === 0) {
      throw new Error('Cannot remove the last user from the board');
    }

    const { data, error } = await supabase
      .from('tasks_board')
      .update({ assigned_users: updatedUsers })
      .eq('id', boardId)
      .select()
      .single();

    if (error) {
      console.error('Error removing user from board:', error);
      throw new Error('Failed to remove user from board');
    }

    return data;
  } catch (error) {
    console.error('Error in removeUserFromBoard:', error);
    throw error;
  }
}

/**
 * Delete board
 */
export async function deleteBoard(boardId: string): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  try {
    const { error } = await supabase
      .from('tasks_board')
      .delete()
      .eq('id', boardId);

    if (error) {
      console.error('Error deleting board:', error);
      throw new Error('Failed to delete board');
    }
  } catch (error) {
    console.error('Error in deleteBoard:', error);
    throw error;
  }
}

/**
 * Set a board as default (and unset all others for the business)
 */
export async function setDefaultBoard(boardId: string, isDefault: boolean): Promise<TasksBoard> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  const { data, error } = await supabase
    .from('tasks_board')
    .update({ is_default: isDefault })
    .eq('id', boardId)
    .select(`
      id,
      business_id,
      board_title,
      assigned_users,
      is_default,
      status_filter,
      created_at,
      updated_at
    `)
    .single();

  if (error) {
    console.error('Error setting default board:', error);
    throw new Error(`Failed to set default board: ${error.message}`);
  }

  console.log('Default board updated successfully:', data);
  return data;
}

/**
 * Get the default board for a business
 */
export async function getDefaultBoard(businessId: string, userInfo: UserTypeInfo): Promise<TasksBoard | null> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Get all default boards for the business (should only be one due to DB constraint)
    const { data: boards, error } = await supabase
      .from('tasks_board')
      .select(`
        id,
        business_id,
        board_title,
        assigned_users,
        is_default,
        status_filter,
        created_at,
        updated_at
      `)
      .eq('business_id', businessId)
      .eq('is_default', true);

    if (error) {
      console.error('Error fetching default board:', error);
      throw new Error(`Failed to fetch default board: ${error.message}`);
    }

    if (!boards || boards.length === 0) {
      return null; // No default board set
    }

    const defaultBoard = boards[0];

    // Check if user has access to this board
    if (userInfo.userType === 'admin') {
      // Admin has access to all boards
      return defaultBoard;
    } else if (userInfo.userType === 'resource' && userInfo.resourceId) {
      // Check if resource is assigned to this board
      const hasAccess = defaultBoard.assigned_users.some((user: BoardUser) => 
        user.type === 'resource' && user.id === userInfo.resourceId
      );
      
      if (hasAccess) {
        return defaultBoard;
      }
    }

    return null; // User doesn't have access to the default board
  } catch (error) {
    console.error('Error in getDefaultBoard:', error);
    return null;
  }
}

/**
 * Update board status filter
 */
export async function updateBoardStatusFilter(
  boardId: string,
  statusFilter: Record<TaskCard['status'], boolean>
): Promise<TasksBoard> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }
  
  try {
    const { data, error } = await supabase
      .from('tasks_board')
      .update({
        status_filter: statusFilter,
        updated_at: new Date().toISOString()
      })
      .eq('id', boardId)
      .select()
      .single();
    
    if (error) {
      console.error('Error updating board status filter:', error);
      throw new Error(`Failed to update board status filter: ${error.message}`);
    }
    
    return data;
  } catch (error) {
    console.error('Error in updateBoardStatusFilter:', error);
    throw error;
  }
}

/**
 * Get all dashboard data in one optimized call (direct Supabase)
 */
export async function getDashboardData(
  businessId: string,
  userId: string
): Promise<DashboardData> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  try {
    // Step 1 & 2: Run user type detection and boards query in parallel
    const [resourceResponse, boardsResponse] = await Promise.all([
      supabase
        .from('resources')
        .select('id, name')
        .eq('auth_user_id', userId)
        .eq('business_id', businessId)
        .single(),
      supabase
        .from('tasks_board')
        .select(`
          id,
          business_id,
          board_title,
          assigned_users,
          is_default,
          status_filter,
          created_at,
          updated_at
        `)
        .eq('business_id', businessId)
    ]);

    // Process user type info
    const userInfo: UserTypeInfo = resourceResponse.data 
      ? {
          userType: 'resource',
          resourceId: resourceResponse.data.id,
          resourceName: resourceResponse.data.name
        }
      : {
          userType: 'admin',
          resourceId: null,
          resourceName: undefined
        };

    // Check for boards query errors
    if (boardsResponse.error) {
      console.error('Error fetching boards:', boardsResponse.error);
      throw new Error('Failed to fetch boards');
    }

    const allBoards = boardsResponse.data;

    // Step 3: Filter boards based on user permissions
    const userBoards = (allBoards || []).filter(board => {
      return board.assigned_users.some((user: BoardUser) => {
        if (userInfo.userType === 'admin') {
          return user.type === 'business';
        } else {
          return user.type === 'resource' && user.id === userInfo.resourceId;
        }
      });
    });

    // Step 4: Find default board
    let defaultBoard: TasksBoard | null = null;
    const defaultBoardCandidate = userBoards.find(board => board.is_default);
    
    if (defaultBoardCandidate) {
      // Check if user has access to default board
      if (userInfo.userType === 'admin') {
        defaultBoard = defaultBoardCandidate;
      } else if (userInfo.userType === 'resource' && userInfo.resourceId) {
        const hasAccess = defaultBoardCandidate.assigned_users.some((user: BoardUser) => 
          user.type === 'resource' && user.id === userInfo.resourceId
        );
        if (hasAccess) {
          defaultBoard = defaultBoardCandidate;
        }
      }
    }

    // Note: Board data (columns/cards) will be loaded lazily when board is clicked

    // Return all data in one response
    const response: DashboardData = {
      boards: userBoards,
      defaultBoard,
      boardData: null, // Board data will be loaded lazily
      userType: userInfo.userType,
      resourceId: userInfo.resourceId || undefined,
      resourceName: userInfo.resourceName
    };

    return response;

  } catch (error) {
    console.error('Error in getDashboardData:', error);
    throw error;
  }
}